import axios from "axios";
import { API_BASE_URL } from "../../../environment";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { cachedRequest } from "../../../utils/requestCache";

const checkSalesUserRole = (): {
  isSalesUser: boolean;
  isClientAdmin: boolean;
  userId: number | null;
} => {
  let isSalesUser = false;
  let isClientAdmin = false;
  let userId = null;

  const authResponse = localStorage.getItem("authResponse");

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      const tenantId = localStorage.getItem("selectedTenantId");
      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
        : null;

      isSalesUser = tenantObj?.role_key === "SALESUSER";
      isClientAdmin = tenantObj?.role_key === "CLIENTADMIN";
      userId = auth.user_id || null;
    } catch (e) {
      console.warn(
        "Contact: Could not parse auth response to check for user role",
        e
      );
    }
  }

  return { isSalesUser, isClientAdmin, userId };
};

const getTenantUrl = () => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

  const { isSalesUser, userId } = checkSalesUserRole();

  let url = `${API_BASE_URL}/contact/?tenant_id=${tenantId}&is_active=true`;

  if (isSalesUser && userId) {
    url += `&sales_user_id=${userId}`;
  }

  return url;
};

const addTenantToContact = (contact: Partial<Contact>): Partial<Contact> => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId) {
    throw new Error("Tenant ID not found in localStorage.");
  }

  return {
    ...contact,
    tenant_id: parseInt(tenantId, 10),
  };
};

const normalizeContactData = (contact: Partial<Contact>): Partial<Contact> => {
  const normalizeUserObject = (userData: any, fieldName: string) => {
    if (!userData) return null;

    if (typeof userData === "object") {
      const requiredFields = {
        created_by: ["user_id", "full_name", "avatar_url"],
        updated_by: ["user_id", "full_name", "avatar_url"],
        assigned_user: ["user_id", "full_name", "email", "phone", "avatar_url"],
      };

      const fields = requiredFields[fieldName as keyof typeof requiredFields];
      if (fields) {
        const isValid = fields.every((field) => userData.hasOwnProperty(field));
        if (isValid) {
          return userData;
        }
      }
    }

    if (typeof userData === "number") {
      return userData;
    }

    return null;
  };

  const normalized = {
    ...contact,

    zip_code: (() => {
      const zipCode = contact.zip_code;
      if (!zipCode) return null;
      if (typeof zipCode === "number") return zipCode;
      if (typeof zipCode === "string" && zipCode.trim() !== "") {
        return parseInt(zipCode, 10);
      }
      return null;
    })(),

    company_id:
      contact.company_id !== null && contact.company_id !== undefined
        ? Number(contact.company_id)
        : null,
    assigned_team_id:
      contact.assigned_team_id !== null &&
        contact.assigned_team_id !== undefined
        ? Number(contact.assigned_team_id)
        : null,
    assigned_user: normalizeUserObject(contact.assigned_user, "assigned_user"),
    created_by: normalizeUserObject(contact.created_by, "created_by"),
    updated_by: normalizeUserObject(contact.updated_by, "updated_by"),
    engagement_source:
      contact.engagement_source !== null &&
        contact.engagement_source !== undefined
        ? Number(contact.engagement_source)
        : 1,
    is_active: Boolean(contact.is_active),

    ...(contact.tenant_id !== undefined && {
      tenant_id: Number(contact.tenant_id),
    }),

    salutation: String(contact.salutation || ""),
    first_name: String(contact.first_name || ""),
    last_name: String(contact.last_name || ""),
    primary_email: String(contact.primary_email || ""),
    personal_email: String(contact.personal_email || ""),
    primary_phone: String(contact.primary_phone || ""),
    mobile_phone: String(contact.mobile_phone || ""),
    preferred_language: String(contact.preferred_language || ""),
    job_title: String(contact.job_title || ""),
    address_line1: String(contact.address_line1 || ""),
    address_line2: String(contact.address_line2 || ""),
    city: String(contact.city || ""),
    state: String(contact.state || ""),
    country: String(contact.country || ""),
    source: String(contact.source || ""),
    lifecycle_source_id: contact.lifecycle_source_id !== undefined ? contact.lifecycle_source_id : null,
    work_email: String(contact.work_email || ""),
    work_phone: String(contact.work_phone || ""),
    landline: String(contact.landline || ""),
  };

  return normalized;
};

const getCurrentUserId = (): number | null => {
  try {
    const authResponse = localStorage.getItem("authResponse");
    if (authResponse) {
      const auth = JSON.parse(authResponse);
      if (auth.user_id) return auth.user_id;
      if (auth.user && auth.user.user_id) return auth.user.user_id;
      if (auth.user && auth.user.id) return auth.user.id;
    }
  } catch (error) {
    console.warn("Error getting current user ID:", error);
  }
  return null;
};

export interface Contact {
  contact_id: number;
  crm_entity_id?: number;
  salutation: string;
  first_name: string;
  last_name: string;
  primary_email: string;
  personal_email: string;
  primary_phone: string;
  mobile_phone: string;
  preferred_language: string;
  job_title: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  country: string;
  zip_code: string | number | null;
  source: string;
  lifecycle_source_id?: number | null;
  lifecycle_source?: {
    lifecycle_source_id: number;
    lifecycle_source_key: string;
    lifecycle_source_display_name: string;
    source_order: number;
  } | null;
  engagement_source: number;
  work_email: string;
  work_phone: string;
  landline: string;
  assigned_datetime: string;
  created_datetime: string;
  updated_datetime: string;
  is_active: boolean;
  company_id: number | null;
  assigned_team_id: number | null;
  company_name?: string;
  avatar_url?: string | null;
  company?: {
    company_id: number;
    company_name: string;
    name?: string;
    avatar_url?: string | null;
    currency_code?: string;
  };
  currency_code?: string;
  assigned_user:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string;
  };
  created_by:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  updated_by:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  tenant_id: number;
  tags?: {
    insert: { entity_tag_id: number; entity_tag_name: string }[];
  } | null;
}

export interface PaginatedContactResponse {
  contacts: Contact[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
}

export const extractChangedFields = (
  originalData: Partial<Contact>,
  newData: Partial<Contact>,
  currentUserId: number | null
): Partial<Contact> => {
  const changedFields: Partial<Contact> = {};

  if (currentUserId) {
    changedFields.updated_by = currentUserId;
  }

  Object.keys(newData).forEach((key) => {
    const typedKey = key as keyof Contact;
    const originalValue = originalData[typedKey];
    const newValue = newData[typedKey];

    const skipFields = [
      "contact_id",
      "created_datetime",
      "updated_datetime",
      "assigned_datetime",
      "tenant_id",
      "created_by",
    ];
    if (skipFields.includes(key)) {
      return;
    }

    let isChanged = false;

    if (
      key === "assigned_user" ||
      key === "created_by" ||
      key === "updated_by"
    ) {
      const getComparableValue = (value: any) => {
        if (value === null || value === undefined) return null;
        if (typeof value === "number") return value;
        if (typeof value === "object" && value.user_id) return value.user_id;
        return value;
      };

      const originalComparableValue = getComparableValue(originalValue);
      const newComparableValue = getComparableValue(newValue);
      isChanged = originalComparableValue !== newComparableValue;
    } else {
      if (originalValue === null && newValue === null) {
        isChanged = false;
      } else if (originalValue === null || newValue === null) {
        isChanged = originalValue !== newValue;
      } else {
        isChanged = originalValue !== newValue;
      }
    }

    if (isChanged) {
      (changedFields as any)[typedKey] = newValue;
    }
  });

  return changedFields;
};

export const ContactService = {
  getContactsByPage: async (
    page: number = 1,
    pageSize: number = 12
  ): Promise<PaginatedContactResponse> => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      const authRaw = localStorage.getItem("authResponse");
      const tenantId = localStorage.getItem("tenant_id");

      if (authRaw && tenantId) {
        const auth = JSON.parse(authRaw);

        const tenantObj = Array.isArray(auth.tenants)
          ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
          : null;

        if (tenantObj?.role_key === "SALESUSER" && auth.user_id) {
          params.sales_user_id = auth.user_id;
        }
      }

      const contactsResponse = await axios.get(getTenantUrl(), {
        headers: getAuthHeaders(),
        params,
      });

      let contacts: Contact[] = [];
      let totalCount = 0;

      if (contactsResponse.data.results) {
        contacts = contactsResponse.data.results;
        totalCount = contactsResponse.data.count || contacts.length;
      } else if (Array.isArray(contactsResponse.data)) {
        contacts = contactsResponse.data;
        totalCount = contacts.length;
      } else {
        console.warn("Unexpected API response format:", contactsResponse.data);
        return {
          contacts: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: page,
          hasMore: false,
        };
      }

      try {
        const tenantId = localStorage.getItem("selectedTenantId");
        if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

        const companies: any[] = await cachedRequest(`companies:enrichment:${tenantId}`, async () => {
          const { isSalesUser, userId } = checkSalesUserRole();

          let companyUrl = `${API_BASE_URL}/company/?tenant_id=${tenantId}`;

          if (isSalesUser && userId) {
            companyUrl += `&sales_user_id=${userId}`;
          }
          const companiesResponse = await axios.get(companyUrl, {
            headers: getAuthHeaders(),
            params: {
              page_size: 1000,
              response_mode: "light",
            },
          });

          if (companiesResponse.data.results) {
            return companiesResponse.data.results;
          } else if (Array.isArray(companiesResponse.data)) {
            return companiesResponse.data;
          }
          return [];
        });

        const companyMap = new Map();
        companies.forEach((company) => {
          if (company.company_id) {
            companyMap.set(company.company_id, {
              company_id: company.company_id,
              company_name: company.company_name,
              name: company.company_name,
              avatar_url: company.avatar_url,
            });
          }
        });

        const contactsWithCompanies = contacts.map((contact) => ({
          ...contact,
          company: contact.company_id
            ? companyMap.get(contact.company_id) || null
            : null,
        }));

        const totalPages = Math.ceil(totalCount / pageSize);
        const hasMore = page < totalPages;

        return {
          contacts: contactsWithCompanies,
          totalCount,
          totalPages,
          currentPage: page,
          hasMore,
        };
      } catch (companyError) {
        console.warn(
          "Error fetching companies for contact population:",
          companyError
        );

        const totalPages = Math.ceil(totalCount / pageSize);
        const hasMore = page < totalPages;

        return {
          contacts,
          totalCount,
          totalPages,
          currentPage: page,
          hasMore,
        };
      }
    } catch (error: any) {
      console.error("Error fetching contacts page:", error);
      return handleAuthError(error);
    }
  },
  checkEmailExists: async (email: string): Promise<boolean> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

      const { isSalesUser, userId } = checkSalesUserRole();

      let checkUrl = `${API_BASE_URL}/contact/?tenant_id=${tenantId}&is_active=true&primary_email=${encodeURIComponent(
        email
      )}`;

      if (isSalesUser && userId) {
        checkUrl += `&sales_user_id=${userId}`;
      }

      const response = await axios.get(checkUrl, {
        headers: getAuthHeaders(),
      });

      if (response.data.results && Array.isArray(response.data.results)) {
        return response.data.results.length > 0;
      }
      return Array.isArray(response.data) && response.data.length > 0;
    } catch (error) {
      console.error("Error checking email from API:", error);
      return false;
    }
  },

  getAllContacts: async (): Promise<Contact[]> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      return await cachedRequest(`contacts:all:${tenantId}`, async () => {
        const response = await ContactService.getContactsByPage(1, 1000);
        return response.contacts;
      });
    } catch (error: any) {
      console.error("Error fetching contacts:", error);
      return handleAuthError(error);
    }
  },

  getContactById: async (id: number): Promise<Contact | null> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

      const { isSalesUser, userId } = checkSalesUserRole();

      const getUrl = `${API_BASE_URL}/contact/${id}/`;
      let params: any = { tenant_id: tenantId };

      if (isSalesUser && userId) {
        params.sales_user_id = userId;
      }
      const response = await axios.get(getUrl, {
        headers: getAuthHeaders(),
        params: params,
      });

      const contact = response.data;

      if (contact && contact.company_id) {
        try {
          const companies: any[] = await cachedRequest(`companies:enrichment:${tenantId}`, async () => {
            const { isSalesUser, userId } = checkSalesUserRole();

            let companyUrl = `${API_BASE_URL}/company/?tenant_id=${tenantId}`;

            if (isSalesUser && userId) {
              companyUrl += `&sales_user_id=${userId}`;
            }
            const companiesResponse = await axios.get(companyUrl, {
              headers: getAuthHeaders(),
              params: {
                page_size: 1000,
                response_mode: "light",
              },
            });

            if (companiesResponse.data.results) {
              return companiesResponse.data.results;
            } else if (Array.isArray(companiesResponse.data)) {
              return companiesResponse.data;
            }
            return [];
          });

          const company = companies.find(
            (comp) => comp.company_id === contact.company_id
          );
          if (company) {
            contact.company = {
              company_id: company.company_id,
              company_name: company.company_name,
              name: company.company_name,
              avatar_url: company.avatar_url,
            };
          }
        } catch (companyError) {
          console.warn("Error fetching company for contact:", companyError);
        }
      }

      return contact;
    } catch (error) {
      console.error("Error fetching contact:", error);
      return handleAuthError(error);
    }
  },

  createContact: async (contact: Partial<Contact>): Promise<Contact> => {
    try {
      const normalizedContact = normalizeContactData(contact);

      const contactWithTenant = addTenantToContact(normalizedContact);

      if (!contactWithTenant.created_by) {
        const currentUserId = getCurrentUserId();
        if (currentUserId) {
          contactWithTenant.created_by = currentUserId;

          contactWithTenant.updated_by = currentUserId;
        }
      } else if (!contactWithTenant.updated_by) {
        contactWithTenant.updated_by = contactWithTenant.created_by;
      }

      const response = await axios.post(
        `${API_BASE_URL}/contact/`,
        contactWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating contact:", error);
      return handleAuthError(error);
    }
  },

  createContactWithAvatar: async (formData: FormData): Promise<Contact> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) throw new Error("Tenant ID not found in localStorage.");
      formData.append("tenant_id", tenantId);

      const currentUserId = getCurrentUserId();
      if (currentUserId && !formData.get("created_by")) {
        formData.append("created_by", currentUserId.toString());

        formData.append("updated_by", currentUserId.toString());
      } else if (
        currentUserId &&
        formData.get("created_by") &&
        !formData.get("updated_by")
      ) {
        formData.append("updated_by", formData.get("created_by") as string);
      }

      const response = await axios.post(`${API_BASE_URL}/contact/`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error: any) {
      console.error("Error creating contact with avatar:", error);
      return handleAuthError(error);
    }
  },

  updateContact: async (
    id: number,
    contact: Partial<Contact>,
    originalContact?: Partial<Contact>
  ): Promise<Contact> => {
    try {
      const currentUserId = getCurrentUserId();

      let updatePayload: Partial<Contact>;

      if (originalContact) {
        updatePayload = extractChangedFields(
          originalContact,
          contact,
          currentUserId
        );

        if (
          Object.keys(updatePayload).length <= 1 &&
          updatePayload.updated_by
        ) {
          return originalContact as Contact;
        }
      } else {
        const normalizedContact = normalizeContactData(contact);
        updatePayload = normalizedContact;

        if (currentUserId) {
          updatePayload.updated_by = currentUserId;
        }
      }

      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

      updatePayload.tenant_id = parseInt(tenantId, 10);

      const updateUrl = `${API_BASE_URL}/contact/${id}/`;
      const response = await axios.patch(updateUrl, updatePayload, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error updating contact:", error);
      return handleAuthError(error);
    }
  },

  updateContactWithAvatar: async (
    id: number,
    formData: FormData,
    originalContact?: Partial<Contact>
  ): Promise<Contact> => {
    try {
      const currentUserId = getCurrentUserId();

      if (formData.has("avatarFile")) formData.delete("avatarFile");
      if (formData.has("avatar")) formData.delete("avatar");

      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) throw new Error("Tenant ID not found in localStorage.");
      if (!formData.has("tenant_id")) {
        formData.append("tenant_id", tenantId);
      }
      if (currentUserId && !formData.has("updated_by")) {
        formData.append("updated_by", currentUserId.toString());
      }

      const updateUrl = `${API_BASE_URL}/contact/${id}/`;
      const response = await axios.patch(updateUrl, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating contact with avatar:", error);
      return handleAuthError(error);
    }
  },

  deleteContact: async (id: number): Promise<void> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

      const { isSalesUser, userId } = checkSalesUserRole();

      const deleteUrl = `${API_BASE_URL}/contact/${id}/`;
      let params: any = { tenant_id: tenantId };

      if (isSalesUser && userId) {
        params.sales_user_id = userId;
      }
      await axios.delete(deleteUrl, {
        headers: getAuthHeaders(),
        params: params,
      });
    } catch (error) {
      console.error("Error deleting contact:", error);
      return handleAuthError(error);
    }
  },
};
